import type { DeviceActivationDto, MobileDeviceDto } from '@bqcrm/shared';
import { Badge, Button, Card, Code, Group, Image, Stack, Table, Text } from '@mantine/core';
import { IconDeviceMobile, IconPlus, IconQrcode } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { useMe } from '../auth';
import { ask } from '../confirm';
import { formatDateTime } from '../format';
import { notifyError, notifyOk } from '../notify';
import { QueryState } from './QueryState';
import { EmptyState } from './ui';

const PLATFORMS: Record<string, string> = { android: 'Android', ios: 'iPhone', web: 'Przeglądarka' };

function Countdown({ until, onEnd }: { until: string; onEnd: () => void }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const left = Math.max(0, Math.round((Date.parse(until) - now) / 1000));
  useEffect(() => {
    if (left === 0) onEnd();
  }, [left, onEnd]);
  return (
    <Text size="sm" c={left < 60 ? 'red' : 'dimmed'}>
      ważny jeszcze {Math.floor(left / 60)}:{String(left % 60).padStart(2, '0')}
    </Text>
  );
}

/**
 * Telefony pracownika z aplikacją Eko CRM: aktywacja nowego telefonu kodem QR (jednorazowy, 15 minut)
 * i lista zarejestrowanych telefonów z unieważnieniem (zgubiony telefon, odejście).
 */
export function MobileDevices({ userId, canRevoke }: { userId: string; canRevoke: boolean }) {
  const qc = useQueryClient();
  const me = useMe();
  const own = me.data?.id === userId;
  const q = useQuery({ queryKey: ['devices', userId], queryFn: () => api<MobileDeviceDto[]>(`/users/${userId}/devices`) });
  const [act, setAct] = useState<DeviceActivationDto | null>(null);
  const create = useMutation({
    mutationFn: () => api<DeviceActivationDto>(`/users/${userId}/device-activations`, { method: 'POST' }),
    onSuccess: setAct,
    onError: notifyError,
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/mobile-devices/${id}/revoke`, { method: 'POST' }),
    onSuccess: () => {
      notifyOk('Unieważniono telefon');
      void qc.invalidateQueries({ queryKey: ['devices', userId] });
    },
    onError: notifyError,
  });
  // nowy telefon pojawia się na liście zaraz po zeskanowaniu kodu
  const known = q.data?.length ?? 0;
  useEffect(() => {
    if (!act) return;
    const t = setInterval(() => void qc.invalidateQueries({ queryKey: ['devices', userId] }), 3000);
    return () => clearInterval(t);
  }, [act, qc, userId]);
  const [base, setBase] = useState<number | null>(null);
  useEffect(() => {
    if (act && base === null) setBase(known);
    if (act && base !== null && known > base) {
      setAct(null);
      setBase(null);
    }
  }, [act, base, known]);

  return (
    <Stack gap="md">
      {act ? (
        <Card withBorder padding="lg">
          <Group align="flex-start" wrap="wrap" gap="lg">
            <Image src={act.qr} w={200} h={200} alt="Kod QR aktywacji aplikacji" style={{ flexShrink: 0 }} />
            <Stack gap={6} style={{ flex: '1 1 240px', minWidth: 0 }}>
              <Text fw={700}>Zeskanuj kod w aplikacji Eko CRM</Text>
              <Text size="sm" c="dimmed">
                {own
                  ? 'Na nowym telefonie uruchom aplikację i wybierz „Skanuj kod QR”. Potem ustawisz PIN (i ewentualnie odcisk palca).'
                  : 'Na nowym telefonie uruchom aplikację i wybierz „Skanuj kod QR”. Potem pracownik ustawi PIN (i ewentualnie odcisk palca).'}
              </Text>
              <Text size="sm" c="dimmed" mt={6}>
                Bez aparatu — wpisz kod ręcznie:
              </Text>
              <Code fz="xl" fw={700} style={{ letterSpacing: 2, alignSelf: 'flex-start' }}>
                {act.code}
              </Code>
              <Text size="xs" c="dimmed">
                serwer: {act.server}
              </Text>
              <Countdown until={act.expiresAt} onEnd={() => setAct(null)} />
              <Group gap="xs" mt={4}>
                <Button size="xs" variant="default" onClick={() => setAct(null)}>
                  Zamknij
                </Button>
                <Button size="xs" variant="subtle" onClick={() => create.mutate()} loading={create.isPending}>
                  Nowy kod
                </Button>
              </Group>
            </Stack>
          </Group>
        </Card>
      ) : (
        <Group justify="space-between" wrap="wrap">
          <Text size="sm" c="dimmed">
            Kod jest jednorazowy i ważny 15 minut. Nowy kod unieważnia poprzedni.
          </Text>
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => create.mutate()} loading={create.isPending}>
            Dodaj telefon
          </Button>
        </Group>
      )}
      <QueryState isLoading={q.isLoading} error={q.error}>
        {!q.data?.length && (
          <EmptyState compact icon={<IconQrcode size={18} />} title="Brak zarejestrowanych telefonów" hint="Dodaj telefon — aplikację mobilną aktywuje się kodem QR." />
        )}
        {!!q.data?.length && (
          <Table.ScrollContainer minWidth={560}>
            <Table>
              <Table.Tbody>
                {q.data.map((d) => (
                  <Table.Tr key={d.id} style={{ opacity: d.revokedAt ? 0.5 : 1 }}>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <IconDeviceMobile size={16} />
                        <Text size="sm">{d.name}</Text>
                        <Badge size="xs" variant="light" style={{ flexShrink: 0 }}>
                          {PLATFORMS[d.platform] ?? d.platform}
                        </Badge>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        dodany {formatDateTime(d.createdAt)} · ostatnio {formatDateTime(d.lastSeenAt)}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      {d.revokedAt ? (
                        <Badge size="sm" variant="light" color="red">
                          unieważniony {formatDateTime(d.revokedAt)}
                        </Badge>
                      ) : (
                        canRevoke && (
                          <Button
                            size="xs"
                            color="red"
                            variant="light"
                            loading={revoke.isPending && revoke.variables === d.id}
                            disabled={revoke.isPending && revoke.variables !== d.id}
                            onClick={() => void ask('Dane zostaną usunięte z telefonu przy jego następnym połączeniu.', { title: 'Unieważnić telefon?', confirm: 'Unieważnij', danger: true }).then((ok) => ok && revoke.mutate(d.id))}
                          >
                            Unieważnij
                          </Button>
                        )
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </QueryState>
    </Stack>
  );
}
